import React, { useState } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { AlertTriangle, Trash2, BatteryCharging } from 'lucide-react';

export default function OilAdminTestTools({ seasonId }) {
  const [ids, setIds] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [topup, setTopup] = useState({ kako_id: '', black_coins: 0, drops: 0, fox_dollars: 0, respect: 0 });
  const [result, setResult] = useState(null);

  const wipe = async () => {
    const list = ids.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error('Informe ao menos um KAKO ID.'); return; }
    if (reason.trim().length < 3) { toast.error('Informe o motivo.'); return; }
    if (confirm !== 'APAGAR') { toast.error('Digite APAGAR para confirmar.'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/oil/season/${seasonId}/test/wipe`,
        { kako_ids: list, confirm, reason });
      setResult(r.data.deleted);
      toast.success(`Dados de ${list.length} conta(s) apagados.`);
      setConfirm('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao apagar.'); }
    setBusy(false);
  };

  const doTopup = async () => {
    if (!topup.kako_id.trim()) { toast.error('Informe o KAKO ID.'); return; }
    if (reason.trim().length < 3) { toast.error('Informe o motivo.'); return; }
    setBusy(true);
    try {
      await api.post(`/admin/oil/season/${seasonId}/test/topup`, { ...topup, reason });
      toast.success('Recursos da conta de teste definidos.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao recarregar.'); }
    setBusy(false);
  };

  return (
    <div className="space-y-3 rounded-xl border-2 border-rose-300 bg-rose-50/50 p-3" data-testid="oil-admin-test-tools">
      <p className="flex items-center gap-2 text-xs font-black uppercase text-rose-700">
        <AlertTriangle className="h-4 w-4" /> Área de risco — ferramentas de teste
      </p>
      <p className="text-[11px] text-rose-700/80">
        Nada aqui apaga a Season nem toca em conta que não for informada. Toda execução exige
        motivo, confirmação por digitação e fica registrada na auditoria.
      </p>

      <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="oil-test-reason"
        placeholder="Motivo (obrigatório para qualquer ação abaixo)"
        className="h-9 w-full rounded-lg border px-2 text-xs" />

      <div className="rounded-lg border bg-white p-3">
        <p className="mb-1 text-xs font-bold text-slate-600">Apagar dados de jogo por KAKO ID</p>
        <textarea value={ids} onChange={(e) => setIds(e.target.value)} data-testid="oil-test-wipe-ids"
          placeholder="Um ou vários KAKO IDs, separados por espaço, vírgula ou linha"
          className="h-20 w-full rounded-lg border p-2 text-xs" />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="oil-test-wipe-confirm"
            placeholder='Digite APAGAR' className="h-9 w-40 rounded-lg border px-2 text-xs" />
          <Button size="sm" variant="destructive" disabled={busy} onClick={wipe} data-testid="oil-test-wipe-submit">
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Apagar dados de jogo
          </Button>
        </div>
        {result && (
          <pre className="mt-2 max-h-32 overflow-auto rounded bg-slate-900 p-2 text-[10px] text-emerald-300"
            data-testid="oil-test-wipe-result">{JSON.stringify(result, null, 2)}</pre>
        )}
      </div>

      <div className="rounded-lg border bg-white p-3">
        <p className="mb-1 text-xs font-bold text-slate-600">Recarregar recursos de conta de teste</p>
        <p className="mb-2 text-[10px] text-slate-500">
          A conta precisa já ter entrado no OIL TYCOON pelo menos uma vez, senão não existe nesta Season.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <input value={topup.kako_id} onChange={(e) => setTopup((p) => ({ ...p, kako_id: e.target.value }))}
            data-testid="oil-test-topup-kako" placeholder="KAKO ID" className="h-9 w-32 rounded-lg border px-2 text-xs" />
          {[['black_coins', 'Black Coins'], ['drops', 'Gotas'], ['fox_dollars', 'FOX Dólar'], ['respect', 'Respeito']].map(([k, label]) => (
            <label key={k} className="text-[10px] font-bold text-slate-500">
              <span className="block">{label}</span>
              <input type="number" min={0} value={topup[k]} data-testid={`oil-test-topup-${k}`}
                onChange={(e) => setTopup((p) => ({ ...p, [k]: +e.target.value }))}
                className="mt-0.5 h-9 w-28 rounded-lg border px-2 text-xs" />
            </label>
          ))}
          <Button size="sm" disabled={busy} onClick={doTopup} data-testid="oil-test-topup-submit"
            className="bg-slate-800 text-white hover:bg-slate-900">
            <BatteryCharging className="mr-1 h-3.5 w-3.5" /> Definir recursos
          </Button>
        </div>
      </div>
    </div>
  );
}
